/**
 * measureSnap — Iter 145 CAD-style snap feedback.
 *
 * Pure functions (no React) that find the best snap target for a finger
 * position during measurement / calibration. Kept stateless so the
 * caller can recompute on every render without churn.
 *
 * Coordinates are normalized (0..1 image space). The threshold is
 * normalized too, so the snap radius "feels" the same at all zoom levels
 * once the operator has zoomed in to read fine geometry.
 *
 * Priority order (lower number = wins):
 *   1. calibration points
 *   2. existing measurement vertices (interior)
 *   3. existing measurement endpoints (first/last of a polyline/area)
 *   4. free placement (returned as null — caller falls back to raw hover)
 */

// Internal constant — ~12–16 px equivalent at default zoom on a 3000-px
// wide raster displayed in a 1024-px viewport. The operator never sees
// this number; it tunes "feel" only.
export const SNAP_THRESHOLD_NORM = 0.014;

export const SNAP_KIND = {
  CALIBRATION: "calibration",
  VERTEX: "vertex",
  ENDPOINT: "endpoint",
};

const PRIORITY = {
  [SNAP_KIND.CALIBRATION]: 1,
  [SNAP_KIND.VERTEX]: 2,
  [SNAP_KIND.ENDPOINT]: 3,
};

const LABEL = {
  [SNAP_KIND.CALIBRATION]: "SNAPPED TO CALIBRATION POINT",
  [SNAP_KIND.VERTEX]: "SNAPPED TO VERTEX",
  [SNAP_KIND.ENDPOINT]: "SNAPPED TO ENDPOINT",
};

/**
 * Build the list of snap targets for the current sheet state.
 *
 * @param {object} args
 * @param {{p1_nx?:number,p1_ny?:number,p2_nx?:number,p2_ny?:number}=} args.sheetScale
 * @param {Array<{nx:number,ny:number}>} [args.measurePts]   in-progress points
 * @param {Array<{points:Array<{nx:number,ny:number}>}>} [args.savedMeasurements]
 * @returns {Array<{nx:number,ny:number,kind:string,priority:number,label:string}>}
 */
export function buildSnapTargets({ sheetScale, measurePts, savedMeasurements }) {
  const out = [];

  // Calibration anchors (highest priority).
  if (sheetScale && Number.isFinite(sheetScale.p1_nx) && Number.isFinite(sheetScale.p1_ny)) {
    out.push({
      nx: sheetScale.p1_nx, ny: sheetScale.p1_ny,
      kind: SNAP_KIND.CALIBRATION, priority: PRIORITY[SNAP_KIND.CALIBRATION],
      label: LABEL[SNAP_KIND.CALIBRATION],
    });
  }
  if (sheetScale && Number.isFinite(sheetScale.p2_nx) && Number.isFinite(sheetScale.p2_ny)) {
    out.push({
      nx: sheetScale.p2_nx, ny: sheetScale.p2_ny,
      kind: SNAP_KIND.CALIBRATION, priority: PRIORITY[SNAP_KIND.CALIBRATION],
      label: LABEL[SNAP_KIND.CALIBRATION],
    });
  }

  // In-progress measurement vertices.
  if (Array.isArray(measurePts)) {
    measurePts.forEach((p) => {
      if (!p || !Number.isFinite(p.nx) || !Number.isFinite(p.ny)) return;
      out.push({
        nx: p.nx, ny: p.ny,
        kind: SNAP_KIND.VERTEX, priority: PRIORITY[SNAP_KIND.VERTEX],
        label: LABEL[SNAP_KIND.VERTEX],
      });
    });
  }

  // Saved measurement points — first/last of each are "endpoints",
  // interior are "vertices". A single-point coordinate measurement is
  // treated as an endpoint (priority 3) too.
  if (Array.isArray(savedMeasurements)) {
    savedMeasurements.forEach((m) => {
      const pts = m && Array.isArray(m.points) ? m.points : [];
      pts.forEach((p, i) => {
        if (!p || !Number.isFinite(p.nx) || !Number.isFinite(p.ny)) return;
        const isEndpoint = i === 0 || i === pts.length - 1;
        const kind = isEndpoint ? SNAP_KIND.ENDPOINT : SNAP_KIND.VERTEX;
        out.push({
          nx: p.nx, ny: p.ny,
          kind, priority: PRIORITY[kind],
          label: LABEL[kind],
        });
      });
    });
  }

  return out;
}

/**
 * Find the highest-priority snap target within range of the given point.
 * Returns null if no target is within the snap threshold.
 *
 * @param {{nx:number,ny:number}|null} point
 * @param {Array<{nx:number,ny:number,priority:number}>} targets
 * @param {number} [threshold=SNAP_THRESHOLD_NORM]
 * @returns {{nx,ny,kind,priority,label,distance}|null}
 */
export function findSnap(point, targets, threshold = SNAP_THRESHOLD_NORM) {
  if (!point || !Number.isFinite(point.nx) || !Number.isFinite(point.ny)) return null;
  if (!Array.isArray(targets) || targets.length === 0) return null;
  // First pass: collect every in-range candidate with its distance.
  const candidates = [];
  for (const t of targets) {
    const d = Math.hypot(t.nx - point.nx, t.ny - point.ny);
    if (d <= threshold) candidates.push({ ...t, distance: d });
  }
  if (candidates.length === 0) return null;
  // Strict priority: lower priority number wins. Among same priority,
  // closer wins. Stable sort ensures deterministic tie-breaks.
  candidates.sort((a, b) => a.priority - b.priority || a.distance - b.distance);
  return candidates[0];
}
